import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { Megaphone, Type, Image as ImageIcon, Clock, Mail, Check } from 'lucide-react';

const SPIN_KEYFRAMES = '@keyframes ap-spin { to { transform: rotate(360deg); } }';

const PRICING_PLANS = [
  {
    key: 'text',
    icon: Type,
    title: 'Text ads',
    description: 'Sleek text-based ads that blend with content.',
  },
  {
    key: 'banner',
    icon: ImageIcon,
    title: 'Banner ads',
    description: 'Eye-catching image banners in standard sizes.',
  },
];

const FEATURES = [
  'Premium placement on the storefront',
  'Click-through tracking & analytics',
  'Real-time performance stats',
  'Custom colors & branding',
  'Mobile-optimised display',
  'Easy campaign management',
];

const PERIODS = [
  { key: 'daily',   label: 'Daily' },
  { key: 'weekly',  label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
];

export default function AdvertisePage() {
  const { slug } = useParams();
  const context = useOutletContext();
  const store = context?.store;
  const theme = context?.theme;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pricingData, setPricingData] = useState(null);

  const primaryColor = theme?.colors?.primary || '#0d7463';

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const storeSlug = slug || store?.slug;
        if (!storeSlug) {
          setError('Store information not available');
          setLoading(false);
          return;
        }
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${backendUrl}/api/storefront/ads/${storeSlug}/pricing`);
        const data = await response.json();
        if (data.success) {
          setPricingData(data);
        } else {
          setError(data.error || 'Pricing information not available');
        }
      } catch (err) {
        console.error('Error fetching pricing:', err);
        setError('Unable to load pricing information');
      } finally {
        setLoading(false);
      }
    };

    const storeSlug = slug || store?.slug;
    if (storeSlug) {
      fetchPricing();
    } else if (store === null || (context && !store)) {
      setError('Store not found');
      setLoading(false);
    }
  }, [slug, store?.slug, store, context]);

  const formatPrice = (price) => {
    if (!price && price !== 0) return '-';
    const currency = pricingData?.pricing?.currency || 'NGN';
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
    }).format(price);
  };

  if (loading) {
    return (
      <div style={S.page}>
        <div style={S.loadingBox}>
          <div style={{ ...S.spinner, borderTopColor: primaryColor }} />
          <p style={S.muted}>Loading pricing…</p>
        </div>
        <style>{SPIN_KEYFRAMES}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={S.page}>
        <div style={{ ...S.card, ...S.errorCard }}>
          <div style={S.errorIcon}><Megaphone size={22} /></div>
          <h2 style={S.errorTitle}>Pricing not available</h2>
          <p style={S.muted}>{error}</p>
        </div>
      </div>
    );
  }

  const { pricing, bannerSizes, storeName } = pricingData;

  return (
    <div style={S.page}>
      {/* Header */}
      <header style={S.header}>
        <div style={{ ...S.headerIcon, color: primaryColor, background: `${primaryColor}14` }}>
          <Megaphone size={22} />
        </div>
        <h1 style={S.title}>Advertise on {storeName}</h1>
        <p style={S.subtitle}>Reach our audience with targeted advertising.</p>
      </header>

      {/* Pricing cards */}
      <div style={S.pricingGrid}>
        {PRICING_PLANS.map((plan) => {
          const Icon = plan.icon;
          const planPrices = pricing?.[plan.key] || {};
          const hasAny = PERIODS.some((p) => planPrices?.[p.key]);
          if (!hasAny) return null;
          return (
            <article key={plan.key} style={S.card}>
              <div style={S.cardHead}>
                <span style={S.cardIcon}><Icon size={16} /></span>
                <h3 style={S.cardTitle}>{plan.title}</h3>
              </div>
              <p style={S.cardDesc}>{plan.description}</p>
              <ul style={S.priceList}>
                {PERIODS.filter((p) => planPrices?.[p.key]).map((p) => (
                  <li key={p.key} style={S.priceRow}>
                    <span style={S.priceLabel}>
                      <Clock size={13} /> {p.label}
                    </span>
                    <span style={S.priceAmount}>{formatPrice(planPrices[p.key])}</span>
                  </li>
                ))}
              </ul>
            </article>
          );
        })}
      </div>

      {/* Features */}
      <section style={S.card}>
        <h3 style={S.sectionTitle}>What's included</h3>
        <ul style={S.featureGrid}>
          {FEATURES.map((feature, i) => (
            <li key={i} style={S.featureItem}>
              <Check size={14} style={{ color: primaryColor, flexShrink: 0 }} />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Banner sizes */}
      {bannerSizes && Object.keys(bannerSizes).length > 0 && (
        <section style={S.card}>
          <h3 style={S.sectionTitle}>Supported banner sizes</h3>
          <ul style={S.sizeList}>
            {Object.values(bannerSizes).map((size, i) => (
              <li key={i} style={S.sizeChip}>
                {size.label} ({size.width}×{size.height})
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Note */}
      {pricing?.note && (
        <div style={S.note}>
          <strong style={S.noteStrong}>Note:</strong> {pricing.note}
        </div>
      )}

      {/* CTA */}
      {pricing?.contactEmail && (
        <div style={S.ctaWrap}>
          <a
            href={`mailto:${pricing.contactEmail}?subject=Advertising Inquiry for ${storeName}`}
            style={{ ...S.cta, background: primaryColor }}
            onMouseOver={(e) => { e.currentTarget.style.opacity = '0.92'; }}
            onMouseOut={(e)  => { e.currentTarget.style.opacity = '1'; }}
          >
            <Mail size={16} /> Get started — contact us
          </a>
          <p style={S.ctaEmail}>{pricing.contactEmail}</p>
        </div>
      )}

      <style>{SPIN_KEYFRAMES}</style>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Storefront pages are themed per-merchant (theme.colors.primary)
   and don't use the merchant dashboard's prefixed CSS files —
   inline styles let us inject the theme color directly.
   Aesthetic: white cards, 1px gray borders, ~12px radius.
   ───────────────────────────────────────────────────────────── */
const S = {
  page: {
    maxWidth: 900,
    margin: '0 auto',
    padding: '24px 16px 48px',
    color: '#111827',
  },
  loadingBox: {
    minHeight: '40vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid #e5e7eb',
    borderRadius: '50%',
    animation: 'ap-spin 1s linear infinite',
  },
  muted: {
    margin: 0,
    color: '#6b7280',
    fontSize: 13,
  },

  /* Error */
  errorCard: {
    textAlign: 'center',
    padding: '40px 24px',
    maxWidth: 420,
    margin: '40px auto',
  },
  errorIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 44,
    height: 44,
    background: '#fef2f2',
    color: '#b91c1c',
    borderRadius: 999,
    marginBottom: 12,
  },
  errorTitle: {
    margin: '0 0 6px',
    fontSize: 17,
    fontWeight: 600,
    color: '#111827',
  },

  /* Header */
  header: {
    textAlign: 'center',
    marginBottom: 28,
  },
  headerIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    borderRadius: 12,
    marginBottom: 14,
  },
  title: {
    margin: '0 0 6px',
    fontSize: 24,
    fontWeight: 700,
    color: '#111827',
    letterSpacing: '-0.01em',
    lineHeight: 1.25,
  },
  subtitle: {
    margin: 0,
    fontSize: 14,
    color: '#6b7280',
  },

  /* Cards (shared) */
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 16,
    marginBottom: 14,
  },
  cardHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 6,
  },
  cardIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
    height: 30,
    borderRadius: 8,
    background: '#f3f4f6',
    color: '#374151',
    flexShrink: 0,
  },
  cardTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: 600,
    color: '#111827',
  },
  cardDesc: {
    margin: '0 0 12px',
    fontSize: 13,
    color: '#6b7280',
    lineHeight: 1.5,
  },

  /* Pricing grid */
  pricingGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: 14,
    marginBottom: 14,
  },
  priceList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  priceRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    padding: '10px 0',
    borderTop: '1px solid #f3f4f6',
    fontSize: 13.5,
  },
  priceLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    color: '#6b7280',
  },
  priceAmount: {
    fontWeight: 600,
    color: '#111827',
    fontSize: 15,
  },

  /* Section title (inside generic card) */
  sectionTitle: {
    margin: '0 0 12px',
    fontSize: 14,
    fontWeight: 600,
    color: '#111827',
  },

  /* Features */
  featureGrid: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 8,
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: '#374151',
    fontSize: 13,
  },

  /* Banner-size chips */
  sizeList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  sizeChip: {
    padding: '4px 10px',
    background: '#f3f4f6',
    border: '1px solid #e5e7eb',
    borderRadius: 999,
    fontSize: 12,
    color: '#374151',
    fontWeight: 500,
  },

  /* Note */
  note: {
    padding: '14px 16px',
    background: '#fffbeb',
    border: '1px solid #fcd34d',
    borderRadius: 10,
    fontSize: 13,
    color: '#92400e',
    lineHeight: 1.5,
    marginBottom: 20,
  },
  noteStrong: {
    color: '#78350f',
    fontWeight: 600,
  },

  /* CTA */
  ctaWrap: {
    textAlign: 'center',
    marginTop: 8,
  },
  cta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 22px',
    color: '#fff',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    textDecoration: 'none',
    transition: 'opacity 0.15s',
  },
  ctaEmail: {
    marginTop: 8,
    color: '#6b7280',
    fontSize: 12,
  },
};
